import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/data/site";

export default function Login({ admin = false }) {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(user.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-24 ${admin ? "bg-brand-green" : ""}`}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-[2rem] p-8 sm:p-10 border border-black/5 shadow-xl">
          <div className="flex justify-center"><img src={LOGO_URL} alt="EXORA" className="h-12" /></div>
          <div className="text-center mt-6">
            {admin && <div className="inline-flex items-center gap-2 text-brand-gold text-xs font-semibold uppercase tracking-widest"><ShieldCheck className="h-4 w-4" /> Staff Portal</div>}
            <h1 className="font-heading text-2xl font-extrabold text-brand-green mt-2">{admin ? "Admin Sign In" : "Welcome Back"}</h1>
            <p className="text-brand-muted text-sm mt-2">{admin ? "Access the EXORA management dashboard." : "Sign in to track your appointments and invoices."}</p>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div><Label className="text-brand-charcoal">Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12 rounded-xl" placeholder="you@email.com" /></div>
            <div><Label className="text-brand-charcoal">Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2 h-12 rounded-xl" placeholder="••••••••" /></div>
            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-brand-green text-white font-bold hover:bg-brand-greenHover">
              {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Signing in...</> : <><Lock className="h-4 w-4 mr-2" /> Sign In</>}
            </Button>
          </form>
          {!admin && (
            <>
              <div className="flex items-center gap-3 my-6">
                <span className="h-px flex-1 bg-black/10" />
                <span className="text-xs text-brand-muted uppercase tracking-widest">or</span>
                <span className="h-px flex-1 bg-black/10" />
              </div>
              <Button type="button" data-testid="google-login-btn" onClick={loginWithGoogle} variant="outline"
                className="w-full h-12 rounded-full border-black/10 font-semibold text-brand-charcoal hover:bg-brand-light">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-2" />
                Continue with Google
              </Button>
              <p className="text-center text-sm text-brand-muted mt-6">New customer? <Link to="/register" className="text-brand-green font-semibold hover:text-brand-gold">Create an account</Link></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
